function hasOwn(target, key) {
  return Object.prototype.hasOwnProperty.call(target, key)
}

export function normalizeDateLimit(limit, defaultUnit = 'day') {
  if (limit === false || limit === null || typeof limit === 'undefined') {
    return null
  }

  if (typeof limit === 'number' || typeof limit === 'string') {
    const value = Number(limit)
    return value > 0 ? {value, unit: defaultUnit} : null
  }

  if (typeof limit === 'object') {
    let rawValue = limit.limit
    if (typeof limit.value !== 'undefined') {
      rawValue = limit.value
    } else if (typeof limit.amount !== 'undefined') {
      rawValue = limit.amount
    }
    const value = Number(rawValue)
    if (!(value > 0)) {
      return null
    }
    return {
      value,
      unit: limit.unit || defaultUnit,
    }
  }

  return null
}

export function normalizeVipRule(vipRule) {
  if (vipRule === false || vipRule === null || typeof vipRule === 'undefined') {
    return {enabled: false}
  }

  const config = vipRule === true ? {} : vipRule
  return {
    enabled: true,
    nonVipLookback: hasOwn(config, 'nonVipLookback')
      ? normalizeDateLimit(config.nonVipLookback, 'day')
      : null,
    vipLookback: hasOwn(config, 'vipLookback')
      ? normalizeDateLimit(config.vipLookback, 'day')
      : null,
    nonVipRange: hasOwn(config, 'nonVipRange')
      ? normalizeDateLimit(config.nonVipRange, 'month')
      : normalizeDateLimit(2, 'month'),
    vipRange: hasOwn(config, 'vipRange')
      ? normalizeDateLimit(config.vipRange, 'month')
      : normalizeDateLimit(12, 'month'),
  }
}

export function normalizeQueryDateConfig(item = {}) {
  const rawConfig = item.disabledDateConfig || {}
  const rawMode = rawConfig.queryMode
  const hasMode = typeof rawMode !== 'undefined'
  const validMode = rawMode === 'standard' || rawMode === 'custom'
  const invalidMode = hasMode && !validMode
  const config = invalidMode ? {} : rawConfig
  const isCustomMode = rawMode === 'custom'
  const firstSelectRange = isCustomMode
    ? hasOwn(config, 'firstSelectRange')
      ? normalizeDateLimit(config.firstSelectRange, 'month')
      : hasOwn(config, 'rangeLimit')
        ? normalizeDateLimit(config.rangeLimit, 'month')
        : normalizeDateLimit(item.isDateLimit, 'month')
    : null

  return {
    queryMode: isCustomMode ? 'custom' : 'standard',
    minDate: config.minDate || config.beforeDateLimit || item.beforeDateLimit || null,
    maxDate: config.maxDate || config.afterDateLimit || item.afterDateLimit || null,
    firstSelectRange,
    vipRule: isCustomMode && hasOwn(config, 'vipRule')
      ? normalizeVipRule(config.vipRule)
      : normalizeVipRule(true),
  }
}

function resolveBoundary(boundary, dayjs, now) {
  if (!boundary) {
    return null
  }

  if (typeof boundary === 'object' && hasOwn(boundary, 'anchor')) {
    const anchor = boundary.anchor === 'today' ? now.startOf('day') : now
    const offset = Number(boundary.offset || 0)
    return anchor.add(offset, boundary.unit || 'day')
  }

  return dayjs(boundary)
}

function isOutsideRelativeRange(date, baseDate, limit, dayjs) {
  if (!baseDate || !limit) {
    return false
  }
  const base = dayjs(baseDate)
  const start = base.subtract(limit.value, limit.unit || 'day')
  const end = base.add(limit.value, limit.unit || 'day')
  return date.isBefore(start, 'day') || date.isAfter(end, 'day')
}

export function isQueryDateDisabled(time, context = {}) {
  const dayjs = context.dayjs
  if (typeof dayjs !== 'function' || !time) {
    return false
  }

  const date = dayjs(time)
  if (!date.isValid()) {
    return false
  }

  const now = context.now ? dayjs(context.now) : dayjs()
  const config = normalizeQueryDateConfig(context.item || {})
  const minDate = resolveBoundary(config.minDate, dayjs, now)
  const maxDate = resolveBoundary(config.maxDate, dayjs, now)

  if (minDate && minDate.isValid() && date.isBefore(minDate, 'day')) {
    return true
  }
  if (maxDate && maxDate.isValid() && date.isAfter(maxDate, 'day')) {
    return true
  }
  if (isOutsideRelativeRange(date, context.firstTime, config.firstSelectRange, dayjs)) {
    return true
  }

  const vipRule = config.vipRule
  if (!vipRule.enabled) {
    return false
  }

  const lookbackAnchor = maxDate && maxDate.isValid() ? maxDate : now
  if (context.isVip === true) {
    if (vipRule.vipLookback) {
      const vipMinDate = lookbackAnchor.subtract(
        vipRule.vipLookback.value,
        vipRule.vipLookback.unit || 'day'
      )
      if (date.isBefore(vipMinDate, 'day')) {
        return true
      }
    }
    return isOutsideRelativeRange(date, context.firstTime, vipRule.vipRange, dayjs)
  }

  if (vipRule.nonVipLookback) {
    const nonVipMinDate = lookbackAnchor.subtract(
      vipRule.nonVipLookback.value,
      vipRule.nonVipLookback.unit || 'day'
    )
    if (date.isBefore(nonVipMinDate, 'day')) {
      return true
    }
  }

  return isOutsideRelativeRange(date, context.firstTime, vipRule.nonVipRange, dayjs)
}
